import { DragDropModule } from '@angular/cdk/drag-drop';
import { LayoutModule } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { I18NextModule } from 'angular-i18next';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { QuillModule } from 'ngx-quill';
import { AumicoCoreConfig, AumicoCoreConfigService } from './aumico-core.config';
import { AumicoButton } from './components/button/aumico.button.component';
import { AumicoDragDropComponent } from './components/drag-drop/aumico-drag-drop.component';
import { EditSectionTitleComponent } from './components/edit-section-title-dialog/edit-section-title.dialog';
import { AumicoEditableInformationPopupComponent } from './components/editable-information-popup/editable-information-popup.component';
import { AumicoDropFileDirective } from './components/file-upload/drop-file.directive';
import { AumicoFileUploadComponent } from './components/file-upload/file-upload.component';
import { FormComponent } from './components/form/form.component';
import { AumicoInformationPopupComponent } from './components/information-popup/information-popup.component';
import { InlineTableCellEditComponent } from './components/inline-table-cell-edit/inline-table-cell-edit.component';
import { ModalComponent } from './components/modal/modal.component';
import { SessionExpirationBannerComponent } from './components/session-expiration-banner/session-expiration-banner.component';
import { SwitchToggleComponent } from './components/switch-toggle/switch-toggle.component';
import { TableComponent } from './components/table/table.component';
import {
    ANNOUNCEMENTS_FEATURE_KEY,
    CONFIGURATIONS_FEATURE_KEY,
    CORE_FEATURE_KEY,
    USER_NOTIFICATIONS_FEATURE_KEY,
    SURVEYS_FEATURE_KEY,
} from './constants/state-keys';
import { EllipsisDirective } from './directives/ellipsis.directive';
import { EnsureNegativeFrontDirective } from './directives/ensure-negative-sign-place.directive';
import { StopPropagationDirective } from './directives/stop-propagation.directive';
import { StylePreferenceDirective } from './directives/stylePreference.directive';
import { AnnouncementsEffects } from './effects/announcements.effect';
import { ConfigurationsEffects } from './effects/configurations.effect';
import { SurveysEffects } from './effects/surveys.effect';
import { UserNotificationsEffects } from './effects/user-notifications.effects';
import { AumicoLayoutModule } from './layout/layout.module';
import { AumicoPatternModule } from './pattern/aumico-pattern.module';
import { KeyForPropertyPipe } from './pipes/KeyForPropertyPipe';
import { ValueForLanguagePipe } from './pipes/ValueForLanguagePipe';
import { AumFinancialNumberPipe } from './pipes/aumFinancialNumber.pipe';
import { DateLocalePipe } from './pipes/dateLocale.pipe';
import { DateTimeLocalePipe } from './pipes/dateTimeLocal.pipe';
import { I18NextWithLocalePipe } from './pipes/i18-next-with-locale.pipe';
import { JoinArrayPipe } from './pipes/join-array.pipe';
import { MarkdownBoldPipe } from './pipes/mark-down-bold.pipe';
import { ReplacePipe } from './pipes/replace.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { TimeAgoPipe } from './pipes/time-ago.pipe';
import * as fromAnnouncementsReducer from './reducers/announcements.reducer';
import * as fromConfigurationsReducer from './reducers/configurations.reducer';
import { coreReducers } from './reducers/core-reducers';
import { userNotificationsReducer } from './reducers/user-notifications.reducer';
import * as fromSurveysReducer from './reducers/surveys.reducer';
import { AnnouncementsService } from './services/announcements.service';
import { AumicoClipboardService } from './services/aumico-clipboard.service';
import { AumicoPasteService } from './services/aumico-paste.service';
import { ConfigurationsService } from './services/configurations.service';
import { AumicoCoreService } from './services/core.service';
import { SessionExpirationService } from './services/session-expiration.service';
import { SurveysService } from './services/surveys.service';
import { NotificationDrawerComponent } from './components/notification-drawer/notification-drawer.component';

const EXPORTED_DECLARATIONS = [
    // Button
    AumicoButton,
    AumicoDragDropComponent,
    TableComponent,
    FormComponent,
    AumicoFileUploadComponent,
    ModalComponent,
    AumicoDropFileDirective,
    DateLocalePipe,
    DateTimeLocalePipe,
    AumFinancialNumberPipe,
    StylePreferenceDirective,
    AumicoInformationPopupComponent,
    JoinArrayPipe,
    ReplacePipe,
    SafeHtmlPipe,
    StopPropagationDirective,
    I18NextWithLocalePipe,
    EllipsisDirective,
    MarkdownBoldPipe,
    SwitchToggleComponent,
    InlineTableCellEditComponent,
    AumicoEditableInformationPopupComponent,
    EditSectionTitleComponent,
    EnsureNegativeFrontDirective,
    SessionExpirationBannerComponent,
    TimeAgoPipe,
];
@NgModule({
    declarations: [
        ...EXPORTED_DECLARATIONS,
        // The pipes may not be exported
        KeyForPropertyPipe,
        ValueForLanguagePipe,
    ],
    providers: [
        // when it is used as a service
        KeyForPropertyPipe,
        ValueForLanguagePipe,
        DateLocalePipe,
        AumFinancialNumberPipe,
    ],
    imports: [
        CommonModule,
        I18NextModule,
        ReactiveFormsModule,
        AumicoLayoutModule.forRoot(),
        QuillModule.forRoot(),
        MatTableModule,
        MatPaginatorModule,
        MatInputModule,
        MatFormFieldModule,
        MatButtonModule,
        MatSelectModule,
        NgxMatSelectSearchModule,
        MatDatepickerModule,
        MatNativeDateModule,
        HttpClientModule,
        MatSlideToggleModule,
        MatRadioModule,
        MatCheckboxModule,
        MatTabsModule,
        DragDropModule,
        FormsModule,
        MatTooltipModule,
        MatExpansionModule,
        MatButtonToggleModule,
        MatIconModule,
        NotificationDrawerComponent,
        MatDialogModule,
        MatSlideToggleModule,
        MatProgressBarModule,
        AumicoPatternModule,
        LayoutModule,
        StoreModule.forFeature(CORE_FEATURE_KEY, coreReducers),
        StoreModule.forFeature(SURVEYS_FEATURE_KEY, fromSurveysReducer.surveysReducer),
        StoreModule.forFeature(CONFIGURATIONS_FEATURE_KEY, fromConfigurationsReducer.ConfigurationsReducer),
        StoreModule.forFeature(ANNOUNCEMENTS_FEATURE_KEY, fromAnnouncementsReducer.announcementsReducer),
        StoreModule.forFeature(USER_NOTIFICATIONS_FEATURE_KEY, userNotificationsReducer),
        EffectsModule.forFeature([ConfigurationsEffects]),
        EffectsModule.forFeature([AnnouncementsEffects]),
        EffectsModule.forFeature([SurveysEffects]),
        EffectsModule.forFeature([UserNotificationsEffects]),
    ],
    exports: [EXPORTED_DECLARATIONS, AumicoPatternModule, AumicoLayoutModule],
})
export class AumicoCoreModule {
    static forRoot(config: AumicoCoreConfig): ModuleWithProviders<AumicoCoreModule> {
        return {
            ngModule: AumicoCoreModule,
            providers: [
                AumicoCoreService,
                ConfigurationsService,
                AnnouncementsService,
                SurveysService,
                AumicoClipboardService,
                AumicoPasteService,
                SessionExpirationService,
                {
                    provide: AumicoCoreConfigService,
                    useValue: config,
                },
            ],
        };
    }
}
